import {
  ArrowLeft,
  ArrowUpRight,
  BookOpen,
  CheckCircle2,
  ClipboardList,
  Mail,
  MapPin,
  Phone,
  ReceiptText,
  Sparkles,
  WalletCards,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Modal } from '../../components/common/Modal';
import { EmptyState } from '../../components/common/EmptyState';
import { LoadingState } from '../../components/common/LoadingState';
import { getClasses, getCourse, getCourseSignup, getCourses, getTransactions, makeClassStudentVisible, updateCourseSignup } from '../../firebase/firestore';
import { useTenant } from '../../hooks/useTenant';
import { formatCurrency } from '../../utils/currency';
import { formatDate, formatDateTime } from '../../utils/date';
import { sendAdmissionCommunications } from '../../services/communicationService';

function formatStudentName(item) {
  return item?.fullName || `${item?.firstName || ''} ${item?.lastName || ''}`.trim() || '-';
}

function getInitials(name = '') {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
}

function toDate(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function resolveApplicationId(searchParams, routeApplicationId, state) {
  return searchParams.get('applicationId')
    || routeApplicationId
    || state?.applicationId
    || state?.myData
    || '';
}

function normalizePayments(application) {
  const records = Array.isArray(application?.paymentHistory)
    ? application.paymentHistory
    : Array.isArray(application?.payments)
      ? application.payments
      : [];

  return records.map((item, index) => ({
    id: item.id || `${application?.id || 'payment'}-${index}`,
    transactionId: item.transactionId || item.id || '',
    paymentDate: item.paymentDate || item.date || item.paidAt || item.createdAt || null,
    paymentMethod: item.method || item.paymentMethod || 'Posted',
    reference: item.reference || item.receiptNumber || item.invoiceNumber || '-',
    incomeAmount: Number(item.amount || item.paidAmount || item.value || 0),
    status: item.status || 'posted',
    receiptUrl: item.receiptUrl || item.downloadUrl || '',
  }));
}

function normalizeTransactionPayments(application, transactions = []) {
  if (!application?.id) return [];

  const applicationName = formatStudentName(application);
  const applicationEmail = String(application?.email || '').trim().toLowerCase();
  const applicationPhone = String(application?.phone || '').replace(/\s+/g, '').trim();
  const applicationId = String(application.id);

  return transactions
    .filter((item) => String(item.category || 'school_fee') === 'school_fee')
    .filter((item) => {
      const studentId = String(item.studentId || '');
      const studentEmail = String(item.studentEmail || '').trim().toLowerCase();
      const studentPhone = String(item.studentPhone || '').replace(/\s+/g, '').trim();
      const studentName = formatStudentName(item);
      return (
        studentId === applicationId
        || (applicationEmail && studentEmail === applicationEmail)
        || (applicationPhone && studentPhone === applicationPhone)
        || normalizeStatusText(studentName) === normalizeStatusText(applicationName)
      );
    })
    .map((item) => ({
      id: item.id,
      transactionId: item.id,
      paymentDate: item.paymentDate || item.date || item.paidAt || item.createdAt || null,
      paymentMethod: item.paymentMethod || item.method || 'Posted',
      reference: item.reference || item.receiptNumber || item.invoiceNumber || '-',
      incomeAmount: Number(item.amount || item.paidAmount || item.value || 0),
      status: item.status || 'posted',
      receiptUrl: item.receiptUrl || item.downloadUrl || '',
    }));
}

function normalizeStatusText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function uniquePayments(payments = []) {
  const seen = new Set();
  return payments.filter((payment) => {
    const key = payment.transactionId
      ? `tx:${payment.transactionId}`
      : `fallback:${normalizeStatusText(payment.reference)}:${Number(payment.incomeAmount || 0)}:${String(payment.paymentDate || '')}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function resolveDueDate(application) {
  const storedDueDate = application?.paymentDueDate || application?.dueDate || application?.nextPaymentDueDate;
  if (storedDueDate) return toDate(storedDueDate);

  const createdAt = toDate(application?.createdAt);
  if (!createdAt) return null;

  const derived = new Date(createdAt);
  derived.setDate(derived.getDate() + 30);
  return derived;
}

function formatAge(yearOfBirth) {
  const year = Number(yearOfBirth);
  if (!year) return 'N/A';
  const age = new Date().getFullYear() - year;
  return Number.isFinite(age) && age > 0 ? `${age} years` : 'N/A';
}

function formatStatusText(value) {
  if (!value) return 'New';
  return String(value)
    .replace(/[-_]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

function toInputDateValue(value) {
  const date = toDate(value);
  if (!date) return '';
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

function classMatchesAppliedSession(classItem, application) {
  if (application?.intakeSessionId && classItem.intakeSessionId) {
    return application.intakeSessionId === classItem.intakeSessionId;
  }
  const selected = String(application?.intakeSessionLabel || '').trim().toLowerCase();
  if (!selected || selected === 'open session') return true;
  if (classItem.intakeSessionLabel && classItem.intakeSessionLabel.toLowerCase() === selected) return true;
  const formatTime = (value) => {
    const [hoursPart, minutesPart = '00'] = String(value || '').split(':');
    const hours = Number(hoursPart);
    if (!Number.isFinite(hours)) return '';
    return `${hours % 12 || 12}:${minutesPart} ${hours >= 12 ? 'pm' : 'am'}`;
  };
  const start = formatTime(classItem.startTime);
  const end = formatTime(classItem.endTime);
  return Boolean(start && end && selected.includes(start) && selected.includes(end));
}

const notJoiningReasons = [
  'Joined another academy',
  'Fees are too high',
  'Timing is not suitable',
  'Could not be reached',
  'No longer interested',
  'Other',
];

const lifecycleReasons = [
  'Personal reasons',
  'Financial reasons',
  'Health reasons',
  'Work commitments',
  'Transferred to another academy',
  'Other',
];

export function ViewStudentPage() {
  const { applicationId: routeApplicationId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { activeTenantId, activeTenant } = useTenant();
  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState(null);
  const [course, setCourse] = useState(null);
  const [courses, setCourses] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [admitOpen, setAdmitOpen] = useState(false);
  const [changeCourseOpen, setChangeCourseOpen] = useState(false);
  const [notJoiningOpen, setNotJoiningOpen] = useState(false);
  const [lifecycleOpen, setLifecycleOpen] = useState(false);
  const [lifecycleAction, setLifecycleAction] = useState('');
  const [savingAction, setSavingAction] = useState('');
  const [admissionDate, setAdmissionDate] = useState('');
  const [classStartDate, setClassStartDate] = useState('');
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [notJoiningReason, setNotJoiningReason] = useState('');
  const [notJoiningNote, setNotJoiningNote] = useState('');
  const [lifecycleDate, setLifecycleDate] = useState('');
  const [lifecycleReturnDate, setLifecycleReturnDate] = useState('');
  const [lifecycleReason, setLifecycleReason] = useState('');
  const [lifecycleNote, setLifecycleNote] = useState('');

  const applicationId = useMemo(
    () => resolveApplicationId(searchParams, routeApplicationId, location.state),
    [location.state, routeApplicationId, searchParams],
  );

  const load = useCallback(async () => {
    if (!activeTenantId || !applicationId) {
      setApplication(null);
      setCourse(null);
      setCourses([]);
      setTransactions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const [nextApplication, nextCourses, nextTransactions] = await Promise.all([
      getCourseSignup(activeTenantId, applicationId),
      getCourses(activeTenantId),
      getTransactions(activeTenantId),
    ]);
    if (!nextApplication) {
      setApplication(null);
      setCourse(null);
      setCourses(nextCourses);
      setTransactions([]);
      setLoading(false);
      return;
    }

    let nextCourse = nextCourses.find((item) => item.id === nextApplication.courseId) || null;
    if (!nextCourse && nextApplication.courseId) {
      nextCourse = await getCourse(activeTenantId, nextApplication.courseId);
    }

    setApplication(nextApplication);
    setCourse(nextCourse);
    setCourses(nextCourses);
    setTransactions(nextTransactions);
    setLoading(false);
  }, [activeTenantId, applicationId]);

  useEffect(() => {
    load().catch((error) => {
      console.error(error);
      setLoading(false);
    });
  }, [load]);

  const currency = activeTenant?.settings?.currency || 'KES';
  const displayName = formatStudentName(application);
  const initials = useMemo(() => getInitials(displayName), [displayName]);
  const paymentHistory = useMemo(() => uniquePayments([
    ...normalizePayments(application),
    ...normalizeTransactionPayments(application, transactions),
  ]).sort((a, b) => {
    const left = toDate(a.paymentDate) || new Date(0);
    const right = toDate(b.paymentDate) || new Date(0);
    return right - left;
  }), [application, transactions]);
  const availableCourses = useMemo(
    () => courses.filter((item) => item.status !== 'deleted'),
    [courses],
  );
  const totalFee = Number(course?.feeAmount || application?.courseFeeAmount || application?.totalFee || 0);
  const feePaid = paymentHistory.length
    ? paymentHistory.reduce((sum, item) => sum + Number(item.incomeAmount || 0), 0)
    : Number(application?.feePaidAmount || application?.amountPaid || 0);
  const feeBalance = Math.max(totalFee - feePaid, 0);
  const paymentDueDate = resolveDueDate(application);
  const statusSlug = String(application?.status || 'inactive').toLowerCase().replace(/\s+/g, '-');
  const isAdmittedStudent = normalizeStatusText(application?.status) === 'student';
  const isLearningBreak = normalizeStatusText(application?.status) === 'learning break';
  const aboutText = application?.message
    || `This student applied for ${course?.name || application?.courseName || 'the selected course'} and chose ${application?.studyMode || 'their preferred'} study mode.`;

  function openAdmitModal() {
    setAdmissionDate(toInputDateValue(application?.admissionDate) || toInputDateValue(new Date()));
    setClassStartDate(toInputDateValue(application?.classStartDate || application?.intakeStartDate) || toInputDateValue(new Date()));
    setAdmitOpen(true);
  }

  function openChangeCourseModal() {
    setSelectedCourseId(application?.courseId || course?.id || '');
    setChangeCourseOpen(true);
  }

  function openNotJoiningModal() {
    setNotJoiningReason(application?.notJoiningReason || '');
    setNotJoiningNote(application?.notJoiningNote || '');
    setNotJoiningOpen(true);
  }

  function openLifecycleModal(action) {
    setLifecycleAction(action);
    setLifecycleDate(
      toInputDateValue(
        action === 'graduate'
          ? application?.graduatedAt
          : action === 'learning-break'
            ? application?.learningBreakAt
            : application?.droppedAt,
      ) || toInputDateValue(new Date()),
    );
    setLifecycleReturnDate(toInputDateValue(application?.learningBreakUntil) || '');
    setLifecycleReason(
      action === 'graduate'
        ? ''
        : action === 'learning-break'
          ? application?.learningBreakReason || ''
          : application?.dropReason || '',
    );
    setLifecycleNote(
      action === 'graduate'
        ? application?.graduatedNote || ''
        : action === 'learning-break'
          ? application?.learningBreakNote || ''
          : application?.dropNote || '',
    );
    setLifecycleOpen(true);
  }

  async function handleAdmitSubmit(event) {
    event.preventDefault();
    if (!admissionDate || !classStartDate || !application?.id) return;

    setSavingAction('admit');
    try {
      let assignedClass = null;
      try {
        const availableClasses = await getClasses(activeTenantId);
        const courseClasses = availableClasses.filter((item) => item.courseId === application.courseId && item.status !== 'deleted');
        assignedClass = courseClasses.find((item) => classMatchesAppliedSession(item, application)) || courseClasses[0] || null;
        if (assignedClass) await makeClassStudentVisible(activeTenantId, assignedClass.id);
      } catch (classError) {
        // Admission must still complete if no class has been scheduled yet.
        console.warn('No class could be assigned during admission.', classError);
      }

      await updateCourseSignup(activeTenantId, application.id, {
        status: 'student',
        admissionDate,
        classStartDate,
        admittedAt: new Date().toISOString(),
        assignedClassId: assignedClass?.id || null,
        assignedClassTitle: assignedClass?.title || '',
        assignedClassStartTime: assignedClass?.startTime || '',
        assignedClassEndTime: assignedClass?.endTime || '',
        assignedClassLocationType: assignedClass?.locationType || '',
      });

      const communicationResults = await sendAdmissionCommunications({
        application: {
          ...application,
          academyName: activeTenant?.name || application.academyName,
          courseName: course?.name || application.courseName,
        },
        admissionDate,
        classStartDate,
      });
      communicationResults.forEach((result, index) => {
        if (result.status === 'rejected') {
          console.error(`${index === 0 ? 'Admission email' : 'Admission SMS'} could not be sent`, result.reason);
        }
      });

      setAdmitOpen(false);
      await load();
    } catch (error) {
      console.error('Unable to admit student', error);
    } finally {
      setSavingAction('');
    }
  }

  async function handleChangeCourseSubmit(event) {
    event.preventDefault();
    if (!selectedCourseId || !application?.id) return;

    const nextCourse = availableCourses.find((item) => item.id === selectedCourseId);
    if (!nextCourse) return;

    setSavingAction('change-course');
    try {
      await updateCourseSignup(activeTenantId, application.id, {
        courseId: nextCourse.id,
        courseName: nextCourse.name || '',
        courseCode: nextCourse.code || '',
        courseFeeAmount: Number(nextCourse.feeAmount || 0),
        courseChangedAt: new Date().toISOString(),
      });
      setChangeCourseOpen(false);
      await load();
    } catch (error) {
      console.error('Unable to update course', error);
    } finally {
      setSavingAction('');
    }
  }

  async function handleNotJoiningSubmit(event) {
    event.preventDefault();
    if (!notJoiningReason || !application?.id) return;

    setSavingAction('not-joining');
    try {
      await updateCourseSignup(activeTenantId, application.id, {
        status: 'did-not-join',
        notJoiningReason,
        notJoiningNote,
        notJoiningAt: new Date().toISOString(),
      });
      setNotJoiningOpen(false);
      await load();
    } catch (error) {
      console.error('Unable to update not joining reason', error);
    } finally {
      setSavingAction('');
    }
  }

  async function handleResumeLearning() {
    if (!application?.id) return;

    setSavingAction('resume-learning');
    try {
      await updateCourseSignup(activeTenantId, application.id, {
        status: 'student',
        learningBreakResumedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      await load();
    } catch (error) {
      console.error('Unable to resume learning', error);
    } finally {
      setSavingAction('');
    }
  }

  async function handleLifecycleSubmit(event) {
    event.preventDefault();
    if (!lifecycleAction || !lifecycleDate || !application?.id) return;

    const nextStatus = lifecycleAction === 'graduate'
      ? 'graduated'
      : lifecycleAction === 'learning-break'
        ? 'learning-break'
        : 'dropped';

    const nextValues = {
      status: nextStatus,
      updatedAt: new Date().toISOString(),
    };

    if (lifecycleAction === 'graduate') {
      nextValues.graduatedAt = lifecycleDate;
      nextValues.graduatedNote = lifecycleNote;
    }

    if (lifecycleAction === 'learning-break') {
      nextValues.learningBreakAt = lifecycleDate;
      nextValues.learningBreakUntil = lifecycleReturnDate;
      nextValues.learningBreakReason = lifecycleReason;
      nextValues.learningBreakNote = lifecycleNote;
    }

    if (lifecycleAction === 'drop') {
      nextValues.droppedAt = lifecycleDate;
      nextValues.dropReason = lifecycleReason;
      nextValues.dropNote = lifecycleNote;
    }

    setSavingAction(lifecycleAction);
    try {
      await updateCourseSignup(activeTenantId, application.id, nextValues);
      setLifecycleOpen(false);
      await load();
    } catch (error) {
      console.error(`Unable to update ${lifecycleAction} status`, error);
    } finally {
      setSavingAction('');
    }
  }

  function handleViewReceipt(payment) {
    if (payment?.receiptUrl) {
      window.open(payment.receiptUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    if (payment?.transactionId) {
      window.open(`/dashboard/finance/school-fee-income/${payment.transactionId}/receipt`, '_blank', 'noopener,noreferrer');
    }
  }

  if (loading) return <LoadingState label="Loading student record..." />;

  if (!application) {
    return (
      <div className="student-record-page">
        <Link className="student-record-back" to="/dashboard/applications">
          <ArrowLeft size={15} /> Back to applications
        </Link>
        <EmptyState
          title="Student not found."
          message="This student record may have been removed or the link is missing an application id."
          actionLabel="Back to Applications"
          to="/dashboard/applications"
          icon={ClipboardList}
        />
      </div>
    );
  }

  const paymentHistoryColumns = [
    { key: 'paymentDate', label: 'Payment date', render: (item) => formatDateTime(toDate(item.paymentDate) || item.paymentDate) },
    { key: 'course', label: 'Course', render: () => course?.name || application?.courseName || '-' },
    { key: 'paymentMethod', label: 'Payment method' },
    { key: 'incomeAmount', label: 'Amount', render: (item) => formatCurrency(item.incomeAmount || 0, currency) },
  ];

  return (
    <div className="student-record-page">
      <header className="student-record-page-header">
        <div>
          <Link className="student-record-back" to="/dashboard/applications"><ArrowLeft size={14} /> Applications</Link>
          <p className="student-record-eyebrow"><Sparkles size={13} /> Student record</p>
          <h1>{isAdmittedStudent ? 'Student profile' : 'Review application'}</h1>
        </div>
        <div className="student-record-reference">
          <ClipboardList size={16} />
          <span><small>Application reference</small>{application.id}</span>
        </div>
      </header>

      <section className="student-record-identity-card">
        <div className="student-record-identity-main">
          <span className="student-record-avatar" aria-hidden="true">{initials}</span>
          <div className="student-record-identity-copy">
            <div className="student-record-name-row">
              <h2>{displayName}</h2>
              <span className={`student-record-status student-record-status--${statusSlug}`}>{formatStatusText(application.status || 'New')}</span>
            </div>
            <p>{course?.name || application.courseName || 'Course not set'} <i /> {application.intakeLabel || 'Open intake'}</p>
            <div className="student-record-contact-row">
              {application.email ? <a href={`mailto:${application.email}`}><Mail size={13} />{application.email}</a> : <span><Mail size={13} />Email not provided</span>}
              {application.phone ? <a href={`tel:${String(application.phone).replace(/\s+/g, '')}`}><Phone size={13} />{application.phone}</a> : <span><Phone size={13} />Phone not provided</span>}
              <span><MapPin size={13} />{application.countryOfResidence || application.country || 'Location not provided'}</span>
            </div>
          </div>
        </div>

        <div className="student-record-actions">
          {isLearningBreak ? (
            <>
              <button type="button" className="student-record-action student-record-action--primary" onClick={handleResumeLearning} disabled={savingAction === 'resume-learning'}>
                {savingAction === 'resume-learning' ? 'Saving...' : 'Resume Learning'}
              </button>
              <button type="button" className="student-record-action student-record-action--danger" onClick={() => openLifecycleModal('drop')}>Drop</button>
            </>
          ) : isAdmittedStudent ? (
            <>
              <button type="button" className="student-record-action student-record-action--primary" onClick={() => openLifecycleModal('graduate')}>Graduate</button>
              <button type="button" className="student-record-action student-record-action--secondary" onClick={() => openLifecycleModal('learning-break')}>Learning Break</button>
              <button type="button" className="student-record-action student-record-action--danger" onClick={() => openLifecycleModal('drop')}>Drop</button>
            </>
          ) : (
            <>
              <button type="button" className="student-record-action student-record-action--primary" onClick={openAdmitModal}><CheckCircle2 size={14} /> Admit student</button>
              <button type="button" className="student-record-action student-record-action--secondary" onClick={openChangeCourseModal}>Change course</button>
              <button type="button" className="student-record-action student-record-action--danger" onClick={openNotJoiningModal}>Not joining</button>
            </>
          )}
        </div>
      </section>

      <section className="student-record-metrics" aria-label="Student fee overview">
        <article className="student-record-metric student-record-metric--dark">
          <span><BookOpen size={17} /></span>
          <div>
            <small>Current course</small>
            <strong>{course?.name || application.courseName || 'Not selected'}</strong>
            <p>{application.studyMode || 'Study mode not provided'}</p>
          </div>
          <ArrowUpRight size={15} />
        </article>
        <article className="student-record-metric">
          <span className="is-warm"><WalletCards size={17} /></span>
          <div>
            <small>Total fee</small>
            <strong>{formatCurrency(totalFee, currency)}</strong>
            <p>Expected course income</p>
          </div>
        </article>
        <article className="student-record-metric">
          <span className="is-green"><CheckCircle2 size={17} /></span>
          <div>
            <small>Fee paid</small>
            <strong>{formatCurrency(feePaid, currency)}</strong>
            <p>{paymentHistory.length} recorded payment{paymentHistory.length === 1 ? '' : 's'}</p>
          </div>
        </article>
        <article className="student-record-metric">
          <span className={feeBalance > 0 ? 'is-rose' : 'is-green'}><ReceiptText size={17} /></span>
          <div>
            <small>Outstanding</small>
            <strong>{formatCurrency(feeBalance, currency)}</strong>
            <p>{paymentDueDate ? `Due ${formatDate(paymentDueDate)}` : 'No payment date set'}</p>
          </div>
        </article>
      </section>

      <div className="studentContentGrid">
        <div className="studentCard">
          <div className="studentCardHeader">
            <h6>About the student</h6>
          </div>
          <div className="studentCardBody">
            <p className="aboutTextInfo">{aboutText}</p>
            <p className="studentSectionLabel">Why the student joined</p>
            <p className="aboutTextInfo">{application.reason || application.whyInterested || 'No notes provided.'}</p>

            <div className="studentMetaGrid">
              <div>
                <span>Gender</span>
                <strong>{application.gender || 'N/A'}</strong>
              </div>
              <div>
                <span>Country</span>
                <strong>{application.countryOfResidence || 'N/A'}</strong>
              </div>
              <div>
                <span>Year of Birth</span>
                <strong>{application.yearOfBirth || 'N/A'}</strong>
              </div>
              <div>
                <span>Age</span>
                <strong>{formatAge(application.yearOfBirth)}</strong>
              </div>
              <div>
                <span>Study Mode</span>
                <strong>{application.studyMode || 'N/A'}</strong>
              </div>
              <div>
                <span>Session</span>
                <strong>{application.intakeSessionLabel || 'N/A'}</strong>
              </div>
            </div>
          </div>
        </div>

        <div className="studentCard">
          <div className="studentCardHeader">
            <h6>Student Details</h6>
          </div>
          <div className="studentCardBody">
            <div className="studentDetailRow">
              <span>Application Date</span>
              <strong>{formatDateTime(toDate(application.createdAt))}</strong>
            </div>
            <div className="studentDetailRow">
              <span>Admission Number</span>
              <strong>{application.admissionNumber || 'N/A'}</strong>
            </div>
            <div className="studentDetailRow">
              <span>Email</span>
              <strong>{application.email || '-'}</strong>
            </div>
            <div className="studentDetailRow">
              <span>Phone</span>
              <strong>{application.phone || '-'}</strong>
            </div>
            <div className="studentDetailRow">
              <span>Course</span>
              <strong>{course?.name || application.courseName || '-'}</strong>
            </div>
            <div className="studentDetailRow">
              <span>Course Code</span>
              <strong>{course?.code || '-'}</strong>
            </div>
            <div className="studentDetailRow">
              <span>Cohort</span>
              <strong>{application.intakeLabel || 'Open intake'}</strong>
            </div>
            <div className="studentDetailRow">
              <span>Payment Due</span>
              <strong>{paymentDueDate ? formatDate(paymentDueDate) : 'Not set'}</strong>
            </div>
            <div className="studentDetailRow">
              <span>Status</span>
              <strong>{formatStatusText(application.status || 'new')}</strong>
            </div>
            <div className="studentDetailRow">
              <span>Admission Date</span>
              <strong>{application.admissionDate ? formatDate(toDate(application.admissionDate)) : 'N/A'}</strong>
            </div>
            <div className="studentDetailRow">
              <span>Class Start Date</span>
              <strong>{application.classStartDate ? formatDate(toDate(application.classStartDate)) : 'N/A'}</strong>
            </div>
          </div>
        </div>
      </div>

      <div className="appsTableCard">
        <div className="appsTableHeader">
          <h6>Fee Payment History</h6>
          <span>{paymentHistory.length} payment{paymentHistory.length === 1 ? '' : 's'}</span>
        </div>
        <div className="appsTableWrap">
          <table className="appsTable">
            <thead>
              <tr>
                {paymentHistoryColumns.map((column) => <th key={column.key}>{column.label}</th>)}
                <th>View Receipts</th>
              </tr>
            </thead>
            <tbody>
              {paymentHistory.length ? paymentHistory.map((payment) => (
                <tr key={payment.id}>
                  {paymentHistoryColumns.map((column) => (
                    <td key={column.key}>
                      {column.render ? column.render(payment) : payment[column.key]}
                    </td>
                  ))}
                  <td>
                    <button
                      type="button"
                      className="appsViewBtn"
                      onClick={() => handleViewReceipt(payment)}
                      disabled={!payment.receiptUrl && !payment.transactionId}
                    >
                      View Receipt
                    </button>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="5" className="appsEmptyState">
                    No fee payments recorded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        className="student-action-modal"
        open={admitOpen}
        onClose={() => setAdmitOpen(false)}
        title="Admit student"
        description="Select the admission date and class start date before moving this applicant into the student stage."
      >
        <form className="modal-form" onSubmit={handleAdmitSubmit}>
          <div className="form-grid">
            <label className="form-field">
              Admission date
              <input
                type="date"
                value={admissionDate}
                onChange={(event) => setAdmissionDate(event.target.value)}
                required
              />
            </label>
            <label className="form-field">
              Class start date
              <input
                type="date"
                value={classStartDate}
                onChange={(event) => setClassStartDate(event.target.value)}
                required
              />
            </label>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setAdmitOpen(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={savingAction === 'admit'}>
              {savingAction === 'admit' ? 'Saving...' : 'Admit student'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        className="student-action-modal"
        open={changeCourseOpen}
        onClose={() => setChangeCourseOpen(false)}
        title="Change course"
        description="Select the course this applicant should move to."
      >
        <form className="modal-form" onSubmit={handleChangeCourseSubmit}>
          <label className="form-field">
            Course
            <select
              value={selectedCourseId}
              onChange={(event) => setSelectedCourseId(event.target.value)}
              required
            >
              <option value="">Select a course</option>
              {availableCourses.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}{item.code ? ` (${item.code})` : ''}
                </option>
              ))}
            </select>
          </label>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setChangeCourseOpen(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={savingAction === 'change-course' || !availableCourses.length}>
              {savingAction === 'change-course' ? 'Saving...' : 'Update course'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        className="student-action-modal"
        open={notJoiningOpen}
        onClose={() => setNotJoiningOpen(false)}
        title="Not joining"
        description="Select the reason this applicant will not join."
      >
        <form className="modal-form" onSubmit={handleNotJoiningSubmit}>
          <label className="form-field">
            Reason
            <select
              value={notJoiningReason}
              onChange={(event) => setNotJoiningReason(event.target.value)}
              required
            >
              <option value="">Select a reason</option>
              {notJoiningReasons.map((reason) => (
                <option key={reason} value={reason}>{reason}</option>
              ))}
            </select>
          </label>
          <label className="form-field">
            Note
            <textarea
              value={notJoiningNote}
              onChange={(event) => setNotJoiningNote(event.target.value)}
              placeholder="Add any extra context"
              rows={4}
            />
          </label>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setNotJoiningOpen(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={savingAction === 'not-joining'}>
              {savingAction === 'not-joining' ? 'Saving...' : 'Save reason'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        className="student-action-modal"
        open={lifecycleOpen}
        onClose={() => setLifecycleOpen(false)}
        title={
          lifecycleAction === 'graduate'
            ? 'Graduate student'
            : lifecycleAction === 'learning-break'
              ? 'Learning break'
              : 'Drop student'
        }
        description={
          lifecycleAction === 'graduate'
            ? 'Mark this student as graduated.'
            : lifecycleAction === 'learning-break'
              ? 'Put this student on a learning break.'
              : 'Mark this student as dropped.'
        }
      >
        <form className="modal-form" onSubmit={handleLifecycleSubmit}>
          <div className="form-grid">
            <label className="form-field">
              {lifecycleAction === 'graduate' ? 'Graduation date' : lifecycleAction === 'learning-break' ? 'Break start date' : 'Drop date'}
              <input
                type="date"
                value={lifecycleDate}
                onChange={(event) => setLifecycleDate(event.target.value)}
                required
              />
            </label>
            {lifecycleAction === 'learning-break' ? (
              <label className="form-field">
                Expected return date
                <input
                  type="date"
                  value={lifecycleReturnDate}
                  onChange={(event) => setLifecycleReturnDate(event.target.value)}
                />
              </label>
            ) : null}
          </div>

          {lifecycleAction !== 'graduate' ? (
            <label className="form-field">
              Reason
              <select
                value={lifecycleReason}
                onChange={(event) => setLifecycleReason(event.target.value)}
                required={lifecycleAction !== 'graduate'}
              >
                <option value="">Select a reason</option>
                {lifecycleReasons.map((reason) => (
                  <option key={reason} value={reason}>{reason}</option>
                ))}
              </select>
            </label>
          ) : null}

          <label className="form-field">
            Note
            <textarea
              value={lifecycleNote}
              onChange={(event) => setLifecycleNote(event.target.value)}
              placeholder="Add any extra context"
              rows={4}
            />
          </label>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setLifecycleOpen(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={savingAction === lifecycleAction}>
              {savingAction === lifecycleAction ? 'Saving...' : (lifecycleAction === 'graduate' ? 'Graduate student' : lifecycleAction === 'learning-break' ? 'Save break' : 'Drop student')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export const ApplicationDetailPage = ViewStudentPage;
